/**
 * @file The create account identity container.
 */

import { connect } from 'react-redux';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';
import produce from 'immer';

import { MeWithIdentities } from '../graphql/queries/account';
import { photosUpload } from '../graphql/mutations/photos';
import { identityCreate as identityCreateMutation } from '../graphql/mutations/identity';
import {
  identityCreateReset,
  identityCreate,
} from '../redux/actions/identity-create';
import CreateAccountIdentity, {
  CreateAccountIdentityComponent,
} from '../components/CreateAccountIdentity';
import { openModal } from '../../../actions/modal-actions';

const withUserData = graphql(MeWithIdentities, {
  props: ({ data: { me } }) => ({
    account: me,
  }),
});

const withPhotosUpload = graphql(photosUpload, {
  props: ({ mutate }) => ({
    photosUploadMutation: (accountId, files) =>
      mutate({
        variables: {
          input: {
            account_id: accountId,
            files,
            selfie_verify: true,
          },
        },
      }),
  }),
});

const withIdentityCreate = graphql(identityCreateMutation, {
  props: ({ mutate, ownProps }) => ({
    // This adds the ability on this container to override its
    // `identityCreateMutation` prop by another HOC/Container (kinda like a
    // half-arsed dependency injection).
    // This is used in `AlbumFolderPurchaseCreateAccountIdentityContainer`
    // where the particular flow requires a completely different query
    // than `MeWithIdentities` (currently `MeWithIdentitiesAndSubscribed`)
    // which needs to be updated differently. It also requires a different
    // `identityCreateMutation`.
    //
    // This is a one time thing pending thought on how to manage
    // this kind of scenario, where a single reusable component
    // might need to be used with different queries and mutations. It's probably
    // best to simply create different new/decoupled containers for each use case
    // than allow to override methods like this, so that queries/mutations
    // are always decoupled.
    identityCreateMutation: ownProps.identityCreateMutation
      ? ownProps.identityCreateMutation
      : ({ accountId, firstName, lastName, gender, photoId }) =>
          mutate({
            variables: {
              input: {
                account_id: accountId,
                name: firstName,
                last_name: lastName,
                relationship: 'SECONDARY',
                photo_id: photoId,
                gender,
                selfie_verify: true,
              },
            },
            update: (
              store,
              {
                data: {
                  identityCreate: { identity },
                },
              },
            ) => {
              const data = store.readQuery({ query: MeWithIdentities });

              const updatedData = produce(data, draft => {
                draft.me = {
                  ...data.me,
                  linked_identities: {
                    ...data.me.linked_identities,
                    records: [...data.me.linked_identities.records, identity],
                  },
                };
              });

              store.writeQuery({ query: MeWithIdentities, data: updatedData });
            },
          }),
  }),
});

const mapStateToProps = ({ identityCreate: identityCreateData }) => ({
  loading: identityCreateData.loading,
  error: identityCreateData.error,
  success: identityCreateData.success,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  identityCreate: async ({
    accountId,
    identityFirstName,
    identityLastName,
    gender,
    file,
  }) => {
    return identityCreate({
      accountId,
      identityFirstName,
      identityLastName,
      gender,
      file,
      dispatch,
      photosUploadMutation: ownProps.photosUploadMutation,
      identityCreateMutation: ownProps.identityCreateMutation,
    });
  },
  identityCreateReset: () => {
    dispatch(identityCreateReset());
  },
  openModal: (...args) => {
    dispatch(openModal(...args));
  },
});

export const CreateAccountIdentityComponentContainer = compose(
  withRouter,
  withUserData,
  withPhotosUpload,
  withIdentityCreate,
  connect(mapStateToProps, mapDispatchToProps),
)(CreateAccountIdentityComponent);

export default compose(
  withRouter,
  withUserData,
  withPhotosUpload,
  withIdentityCreate,
  connect(mapStateToProps, mapDispatchToProps),
)(CreateAccountIdentity);
